import { Box, CircularProgress } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { lazy, Suspense, useEffect } from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import { api } from './api';
import { AppearanceProvider } from './appearance';
import i18n, { rememberLocale } from './i18n';

const Login = lazy(() => import('./login').then((module) => ({ default: module.Login })));
const Shell = lazy(() => import('./shell').then((module) => ({ default: module.Shell })));
const OverviewPage = lazy(() =>
  import('./master-data-pages').then((module) => ({ default: module.MasterDataOverview })),
);
const ProductsPage = lazy(() =>
  import('./master-data-pages').then((module) => ({ default: module.ProductsPage })),
);
const StoresPage = lazy(() =>
  import('./master-data-pages').then((module) => ({ default: module.StoresPage })),
);
const NetworksPage = lazy(() =>
  import('./master-data-pages').then((module) => ({ default: module.NetworksPage })),
);
const ProductDetailPage = lazy(() =>
  import('./product-detail').then((module) => ({ default: module.ProductDetailPage })),
);
const StoreDetailPage = lazy(() =>
  import('./store-detail').then((module) => ({ default: module.StoreDetailPage })),
);
const SettingsPage = lazy(() =>
  import('./pages').then((module) => ({ default: module.SettingsPage })),
);
const UsersPage = lazy(() => import('./pages').then((module) => ({ default: module.UsersPage })));
const InventoryPage = lazy(() =>
  import('./inventory-page').then((module) => ({ default: module.InventoryPage })),
);
const SalesImportPage = lazy(() =>
  import('./sales-import-page').then((module) => ({ default: module.SalesImportPage })),
);
const SalesWorkspacePage = lazy(() =>
  import('./sales-workspace-page').then((module) => ({ default: module.SalesWorkspacePage })),
);
const StockWorkspacePage = lazy(() =>
  import('./stock-workspace-page').then((module) => ({ default: module.StockWorkspacePage })),
);
const ShipmentImportPage = lazy(() =>
  import('./shipment-import-page').then((module) => ({ default: module.ShipmentImportPage })),
);
const ShipmentWorkspacePage = lazy(() =>
  import('./shipment-workspace-page').then((module) => ({ default: module.ShipmentWorkspacePage })),
);
const BulkImportPage = lazy(() =>
  import('./bulk-import-page').then((module) => ({ default: module.BulkImportPage })),
);
const DashboardPage = lazy(() =>
  import('./dashboard-page').then((module) => ({ default: module.DashboardPage })),
);
const ProductionPage = lazy(() =>
  import('./production-page').then((module) => ({ default: module.ProductionPage })),
);
const OperationsPage = lazy(() =>
  import('./operations-page').then((module) => ({ default: module.OperationsPage })),
);
const ForecastPage = lazy(() =>
  import('./forecast-page').then((module) => ({ default: module.ForecastPage })),
);
const CostingPage = lazy(() =>
  import('./production-costing-page').then((module) => ({ default: module.ProductionCostingPage })),
);
const ProfitabilityPage = lazy(() =>
  import('./profitability-page').then((module) => ({ default: module.ProfitabilityPage })),
);
const StoreOrdersPage = lazy(() =>
  import('./store-orders-page').then((module) => ({ default: module.StoreOrdersPage })),
);
const MaterialsPage = lazy(() =>
  import('./materials-page').then((module) => ({ default: module.MaterialsPage })),
);
const AbpCatalogPage = lazy(() =>
  import('./abp-catalog-page').then((module) => ({ default: module.AbpCatalogPage })),
);

const Loading = (): React.JSX.Element => (
  <Box sx={{ minHeight: '100dvh', display: 'grid', placeItems: 'center' }}>
    <CircularProgress size={28} />
  </Box>
);

export function App(): React.JSX.Element {
  const session = useQuery({
    queryKey: ['session'],
    queryFn: api.session,
    retry: false,
  });

  useEffect(() => {
    if (session.data?.user.locale) {
      rememberLocale(session.data.user.locale);
      void i18n.changeLanguage(session.data.user.locale);
    }
  }, [session.data?.user.locale]);

  if (session.isPending) {
    return <Loading />;
  }
  if (!session.data) {
    return (
      <Suspense fallback={<Loading />}>
        <Login />
      </Suspense>
    );
  }

  const user = session.data.user;
  const router = createBrowserRouter([
    {
      path: '/',
      element: <Shell user={user} />,
      children: [
        { index: true, element: <Navigate to="dashboard" replace /> },
        { path: 'dashboard', element: <DashboardPage /> },
        { path: 'overview', element: <OverviewPage /> },
        { path: 'products', element: <ProductsPage user={user} /> },
        { path: 'products/:productId', element: <ProductDetailPage user={user} /> },
        ...(user.permissions.includes('VIEW_ABP_CATALOG')
          ? [{ path: 'abp-catalog', element: <AbpCatalogPage user={user} /> }]
          : []),
        { path: 'stores', element: <StoresPage user={user} /> },
        { path: 'stores/:storeId', element: <StoreDetailPage /> },
        { path: 'networks', element: <NetworksPage user={user} /> },
        ...(user.permissions.includes('VIEW_IMPORT_HISTORY')
          ? [
              { path: 'imports/center', element: <BulkImportPage user={user} /> },
              { path: 'imports/center/:batchId', element: <BulkImportPage user={user} /> },
              { path: 'imports/sales', element: <SalesImportPage user={user} /> },
              { path: 'imports/sales/:batchId', element: <SalesImportPage user={user} /> },
              { path: 'imports/shipments', element: <ShipmentImportPage user={user} /> },
              {
                path: 'imports/shipments/:batchId',
                element: <ShipmentImportPage user={user} />,
              },
            ]
          : []),
        { path: 'inventory', element: <InventoryPage key="store-inventory" user={user} /> },
        ...(user.permissions.includes('VIEW_STOCK')
          ? [
              { path: 'stock', element: <StockWorkspacePage /> },
              { path: 'production', element: <ProductionPage user={user} /> },
              { path: 'operations', element: <OperationsPage /> },
            ]
          : []),
        ...(user.permissions.includes('VIEW_SALES')
          ? [{ path: 'sales', element: <SalesWorkspacePage /> }]
          : []),
        ...(user.permissions.includes('VIEW_SHIPMENTS')
          ? [{ path: 'shipments', element: <ShipmentWorkspacePage user={user} /> }]
          : []),
        ...(user.permissions.includes('VIEW_STORE_ORDERS')
          ? [{ path: 'orders', element: <StoreOrdersPage user={user} /> }]
          : []),
        ...(user.permissions.includes('VIEW_FORECAST')
          ? [
              {
                path: 'forecast',
                element: <ForecastPage canEdit={user.permissions.includes('EDIT_FORECAST')} />,
              },
            ]
          : []),
        ...(user.permissions.includes('VIEW_PRODUCTION_COST')
          ? [
              {
                path: 'materials',
                element: (
                  <MaterialsPage
                    canEdit={
                      user.permissions.includes('EDIT_PRODUCTION_COST') &&
                      user.permissions.includes('EDIT_PRODUCT_MASTER')
                    }
                  />
                ),
              },
              {
                path: 'costing',
                element: (
                  <CostingPage
                    canEdit={user.permissions.includes('EDIT_PRODUCTION_COST')}
                    canCreateProduct={user.permissions.includes('EDIT_PRODUCT_MASTER')}
                    canConfigure={
                      user.permissions.includes('EDIT_PRODUCTION') &&
                      user.permissions.includes('EDIT_PRODUCT_MASTER')
                    }
                  />
                ),
              },
              { path: 'profitability', element: <ProfitabilityPage /> },
            ]
          : []),
        {
          path: 'warehouse',
          element: (
            <InventoryPage key="warehouse-inventory" user={user} initialLocationKind="WAREHOUSE" />
          ),
        },
        { path: 'settings', element: <SettingsPage user={user} /> },
        ...(user.permissions.includes('ADMINISTER_USERS')
          ? [{ path: 'users', element: <UsersPage user={user} /> }]
          : []),
        { path: '*', element: <Navigate to="/" replace /> },
      ],
    },
  ]);
  return (
    <Suspense fallback={<Loading />}>
      <AppearanceProvider user={user}>
        <RouterProvider router={router} />
      </AppearanceProvider>
    </Suspense>
  );
}
